/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
// gnoment 🧙‍♂️🍄 - Wrapper using internationalized date & date-fns to look similar to moment.js to ease conversions

// When modifying please add any additional test cases found to shared-ui/gnoment/gnoment.js and ensure all cases pass
// Caveats gnoment currently does not accept a second format param like moment, check if format string is handled before using

import {
    now,
    getLocalTimeZone,
    CalendarDateTime,
    parseDate,
    parseDateTime,
    parseAbsolute,
    toCalendarDateTime,
    toZoned,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    ZonedDateTime,
    toCalendarDate,
    CalendarDate,
    fromAbsolute,
    Time,
    parseTime,
} from '@internationalized/date';

import {
    differenceInDays,
    differenceInMonths,
    differenceInWeeks,
    differenceInYears,
    differenceInHours,
    differenceInMinutes,
    differenceInSeconds,
    differenceInMilliseconds,
} from 'date-fns';
import isNil from 'lodash/isNil';

export const formatRegex =
    /\[([^\]]+)\]|YYYY|YY|MMMM|MMM|MM|M|DD|Do|D|dddd|ddd|HH|H|hh|h|mm|m|ss|s|sss|SSS|A|a|ZZ|Z|z/g;
export const unixMillisecsRegex = /^\d{13}$/;
const zoneOffset = /.*([+-]\d{2}:?\d{2})/;
//Matches MM/DD/YYYY, M/DD/YYYY, MM/D/YYYY, MM-DD-YYYY, M-DD-YYYY, MM-D-YYYY
// Must be checked after YYYY-MM-DD so ISO dates are not misread as short dates
const shortDateRegex = /^\d{1,2}[/-]\d{1,2}[/-]\d{1,4}$/;
//Matches YYYY-MM-DD
const YYYYMMDDDateRegex = /^\d{4}-\d{2}-\d{2}$/;
//Matches YYYY-MM
const YYYYMMDateRegex = /^\d{4}-\d{2}$/;
//Matches HH:mm:ss
const HHMMSSRegex = /^\d{2}:\d{2}:\d{2}$/;
//Matches YYYY
const YYYYRegex = /^\d{4}$/;
//Matches written out date: January 1 or January 1, 2025
const writtenoutDateRegex = /^[A-Za-z]+ \d{1,2}(?:, \d{4})?$/;

const fullDateStringRegex =
    /^[A-Za-z]{3,4}\s[A-Za-z]{3}\s\d{2}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT[+-]\d{4}$/;

export function gnomentToCalendarDate(g, options) {
    let res = null;

    if (g && String(g) !== 'Invalid date') {
        if (options?.useUtc) {
            res = gnoment(g).utc().toCalendarDate();
        } else if (options?.timeZone) {
            res = gnoment(g).tz(options.timeZone).toCalendarDate();
        } else {
            res = gnoment(g).toCalendarDate();
        }
    }

    return res;
}

export function calendarDateToGnoment(g, options) {
    let res = null;
    if (g) {
        if (options?.useUtc) {
            res = gnoment.utc(g);
        } else {
            res = gnoment(g);
        }
    }

    return res;
}

export function dateValueToGnoment(dateValue, options) {
    if (!dateValue) return null;

    const localTimeZone = getLocalTimeZone();
    const timeZoneInValue =
        dateValue && 'timeZone' in dateValue ? dateValue?.timeZone : undefined;
    const resolvedTimeZone = options?.timeZone
        ? options.timeZone
        : (timeZoneInValue ?? localTimeZone);

    const date = dateValue.toDate(resolvedTimeZone);

    if (options?.useUtc) {
        return gnoment.utc(date);
    }

    return gnoment(date);
}

export function rangeValueToGnomentRange(range, options) {
    let startDate = null;
    let endDate = null;

    if (range) {
        startDate = calendarDateToGnoment(range.start, options);
        endDate = calendarDateToGnoment(range.end, options);
    }

    return {
        startDate,
        endDate,
    };
}

export function gnomentRangeToRangeValue(startDate, endDate, options) {
    let rangeValue = null;

    const start = gnomentToCalendarDate(startDate, options);
    const end = gnomentToCalendarDate(endDate, options);

    if (start && end) {
        rangeValue = {
            start,
            end,
        };
    }

    return rangeValue;
}

export function gnomentToCalendarDateTime(g, options) {
    let res;
    if (options?.useUtc) {
        res = gnoment(g).utc().toCalendarDateTime();
    } else if (options?.timeZone) {
        res = gnoment(g).tz(options.timeZone).toCalendarDateTime();
    } else {
        res = gnoment(g).toCalendarDateTime();
    }
    return res;
}

// This function is used to parse a date string in a flexible way, and should always return a ZonedDateTime object.
const parseFlexibleDate = (input, timeZone = 'UTC') => {
    let res;
    let hasNoTime = false;
    if (typeof input === 'number' && String(input).match(unixMillisecsRegex)) {
        res = fromAbsolute(input, timeZone);
    } else if (input.match(writtenoutDateRegex)) {
        let formattedInput = input.trim();
        if (!formattedInput.includes(',')) {
            const currentYear =
                timeZone === 'UTC'
                    ? new Date().getUTCFullYear()
                    : new Date().getFullYear();

            formattedInput = `${formattedInput}, ${currentYear}`;
        }
        const tempDate = new Date(formattedInput);
        const isoDate = tempDate.toISOString().slice(0, 10);

        let d = parseDate(isoDate);
        d = toZoned(d, timeZone);
        d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        res = d;
    } else if (input.match(fullDateStringRegex)) {
        const parts = input.split(' ');
        const month = {
            Jan: 1,
            Feb: 2,
            Mar: 3,
            Apr: 4,
            May: 5,
            Jun: 6,
            Jul: 7,
            Aug: 8,
            Sep: 9,
            Oct: 10,
            Nov: 11,
            Dec: 12,
        }[parts[1]];
        const day = parseInt(parts[2], 10);
        const year = parseInt(parts[3], 10);
        const [hours, minutes, seconds] = parts[4].split(':').map((n) => parseInt(n, 10));
        const dateTime = new CalendarDateTime(year, month, day, hours, minutes, seconds);
        const utcZoned = toZoned(dateTime, 'UTC');
        res = timeZone === 'UTC' ? utcZoned : toZoned(utcZoned, timeZone);
    }
    // If the input is exactly "YYYY-MM-DD", assume it's a date without time
    else if (input.match(YYYYMMDDDateRegex)) {
        hasNoTime = true;
        let d = parseDate(input);
        d = toZoned(d, timeZone);
        d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        res = d;
    }
    // If the input is exactly "YYYY-MM", assume it's a date without time
    else if (input.match(YYYYMMDateRegex)) {
        hasNoTime = true;
        // If there's no day specified, assume the first day of the month
        const inputUpdated = `${input}-01`;
        let d = parseDate(inputUpdated);
        d = toZoned(d, timeZone);
        d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        res = d;
    }
    // If matches short US date format with slashes (/) or dashes (-)
    else if (input.match(shortDateRegex)) {
        hasNoTime = true;
        const date = input.split(/[/-]/);
        const month = date[0].padStart(2, '0');
        const day = date[1].padStart(2, '0');
        let year = date[2];

        const yearNum = parseInt(year, 10);

        // Handle years based on number of digits:
        if (year?.length === 1 || year?.length === 2) {
            // 1-2 digits: Use cutoff year of 68 (same as moment)
            if (yearNum >= 0 && yearNum <= 68) {
                year = `20${year.padStart(2, '0')}`;
            } else {
                year = `19${year.padStart(2, '0')}`;
            }
        } else if (year?.length === 3) {
            // 3 digits: Prepend with 0 (0999 = year 999)
            year = year.padStart(4, '0');
        }

        const isoFormatted = `${year}-${month}-${day}`;
        let d = parseDate(isoFormatted);
        // The way the time is handled for short US dates (MM/DD/YYYY or MM-DD-YYYY) is
        // different than ISO dashes (YYYY-MM-DD) in moment, so matching that behavior here
        if (timeZone !== getLocalTimeZone()) {
            d = toZoned(d, 'UTC');
        }
        d = toZoned(d, timeZone);
        res = d;
    } else if (input.match(YYYYRegex)) {
        hasNoTime = true;
        const isoFormatted = `${input}-01-01`;
        let d = parseDate(isoFormatted);
        d = toZoned(d, timeZone);
        d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        res = d;
    } else if (input.match(HHMMSSRegex)) {
        const timeObj = parseTime(input);
        const today = now(timeZone);
        const dateTime = new CalendarDateTime(
            today.year,
            today.month,
            today.day,
            timeObj.hour,
            timeObj.minute,
            timeObj.second
        );
        res = toZoned(dateTime, timeZone);
    } else if (input.includes('Z') || input.match(zoneOffset)) {
        // If it has a 'Z' (UTC) or timezone offset, treat as absolute timestamp
        res = parseAbsolute(input, timeZone);
    } else {
        // Otherwise, assume it's a local date-time string
        let inputUpdated = input;
        if (input.length > 11 && input[10] === ' ') {
            inputUpdated = `${input.substring(0, 10)}T${input.substring(11)}`;
        }
        const d = parseDateTime(inputUpdated);
        res = toZoned(d, timeZone);
    }
    return { zoned: res, hasNoTime };
};

//Accepts (date, tz) or (tz) params
gnoment.tz = (...params) => {
    let zoneDateTime;

    if (params.length === 2) {
        if (params[0] === undefined) {
            zoneDateTime = gnoment(undefined).tz(params[1] || 'UTC');
        } else if (
            params[0] === null ||
            (typeof params[0] === 'string' && params[0]?.trim() === '')
        ) {
            zoneDateTime = gnoment(null).tz(params[1] || 'UTC');
        } else if (Array.isArray(params[0])) {
            //Assume that date is in the format: [YEAR, MONTH, DAY, HOUR, MINUTE]
            const dateObj = params[0];
            const year = dateObj[0];
            const month = dateObj[1];
            const day = dateObj[2];
            const hour = dateObj[3];
            const minute = dateObj[4];

            const dateTime = new CalendarDateTime(year, month + 1, day, hour, minute);

            zoneDateTime = toZoned(dateTime, params[1] || 'UTC');
        } else if (typeof params[0] === 'string') {
            const { zoned, hasNoTime } = parseFlexibleDate(params[0], params[1] || 'UTC');
            zoneDateTime = zoned;
            const g = gnoment(zoneDateTime);
            g.hasNoTime = hasNoTime;
            return g;
        } else {
            const tz = params[1] !== '' ? params[1] : 'UTC';
            return gnoment(params[0]).tz(tz);
        }

        return gnoment(zoneDateTime);
    }

    if (params.length === 1) {
        return gnoment().tz(params[0] || 'UTC');
    }
    return gnoment(null);
};

let defaultTimeZone = getLocalTimeZone();
let defaultLocale = 'en-US';

gnoment.tz.setDefault = (tz) => {
    defaultTimeZone = tz;
};

gnoment.locale = (locale) => {
    if (locale === undefined) {
        return defaultLocale;
    }
    defaultLocale = locale;
    return defaultLocale;
};

gnoment.utc = (date) => {
    const u = gnoment(date).utc();
    // 🙄 I guess moment().utc() and moment.utc() behave differently
    // see if the date specifies time; if not, set the time to 00:00:00
    if (u.hasNoTime) {
        u.set({ hour: 0, minute: 0, second: 0 });
    }
    return u;
};

gnoment.invalid = () => {
    return gnoment(null);
};

export class Gnoment {
    static formatterCache = {};
    constructor(date) {
        try {
            this.zonedDateTime = null;
            if (date === undefined) {
                this.zonedDateTime = now(defaultTimeZone);
            } else if (typeof date === 'string' && date !== '') {
                const { zoned: dateObj, hasNoTime } = parseFlexibleDate(
                    date,
                    defaultTimeZone
                );
                this.hasNoTime = hasNoTime;
                if (dateObj) {
                    this.zonedDateTime = toCalendarDateTime(dateObj);
                    this.zonedDateTime = toZoned(this.zonedDateTime, defaultTimeZone);
                }
            } else if (
                typeof date === 'number' &&
                String(date).match(unixMillisecsRegex)
            ) {
                this.zonedDateTime = fromAbsolute(date, defaultTimeZone);
            } else if (date instanceof Date) {
                if (!Number.isNaN(date.getFullYear())) {
                    const dateTime = new CalendarDateTime(
                        date.getFullYear(),
                        date.getMonth() + 1,
                        date.getDate(),
                        date.getHours(),
                        date.getMinutes(),
                        date.getSeconds()
                    );
                    this.zonedDateTime = toZoned(dateTime, defaultTimeZone);
                }
            } else if (date instanceof ZonedDateTime) {
                this.zonedDateTime = date;
            } else if (date instanceof CalendarDate) {
                this.zonedDateTime = toZoned(date, defaultTimeZone);
            } else if (date instanceof Gnoment) {
                this.zonedDateTime = date.zonedDateTime;
            } else if (typeof date === 'object' && !Array.isArray(date) && !isNil(date)) {
                if (
                    Object.keys(date).length === 1 &&
                    'seconds' in date &&
                    (Number(date?.seconds) < 0 || Number(date?.seconds) > 59)
                ) {
                    this.zonedDateTime = null;
                } else {
                    // Accept plain objects like {years: '2010', months: '3', date: '5'} or {hours:0}
                    // Use current CalendarDateTime as baseline
                    const baseline = toCalendarDateTime(now(defaultTimeZone));
                    const year =
                        !isNil(date?.year) || !isNil(date?.years)
                            ? Number(date.year || date.years)
                            : baseline.year;
                    const month =
                        !isNil(date?.month) || !isNil(date?.months)
                            ? Number(date.month || date.months) + 1
                            : baseline.month;
                    const day =
                        !isNil(date?.date) || !isNil(date?.day)
                            ? Number(date.date || date.day)
                            : baseline.day;
                    const hour =
                        !isNil(date?.hour) || !isNil(date?.hours)
                            ? Number(date.hour || date.hours)
                            : 0;
                    const minute =
                        !isNil(date?.minute) || !isNil(date?.minutes)
                            ? Number(date.minute || date.minutes)
                            : 0;
                    const second =
                        !isNil(date?.second) || !isNil(date?.seconds)
                            ? Number(date.second || date.seconds)
                            : 0;
                    const calDateTime = new CalendarDateTime(
                        year,
                        month,
                        day,
                        hour,
                        minute,
                        second
                    );
                    this.zonedDateTime = toZoned(calDateTime, defaultTimeZone);
                }
            }

            this.inUTCMode = false;
            this._locale = null;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log('exception creating gnoment: ', e);
            this.zonedDateTime = null;
        }
    }

    toCalendarDate() {
        if (!this.isValid()) return null;
        return toCalendarDate(this.zonedDateTime);
    }

    toCalendarDateTime() {
        return toCalendarDateTime(this.zonedDateTime);
    }

    toZonedDateTime() {
        return this.zonedDateTime;
    }

    getCalendarDateTime() {
        return this.zonedDateTime;
    }

    utc(keepLocalTime = false) {
        if (!this.isValid()) return this;

        let utcDateTime;

        /**
         * Converts locale datetime as utc
         */
        if (keepLocalTime) {
            const date = new CalendarDate(
                this.zonedDateTime.year,
                this.zonedDateTime.month,
                this.zonedDateTime.day
            );
            const time = new Time(this.zonedDateTime.hour, this.zonedDateTime.minute);

            const datetime = toCalendarDateTime(date, time);
            utcDateTime = toZoned(datetime, 'UTC');
        } else {
            utcDateTime = toZoned(this.zonedDateTime, 'UTC');
        }
        this.zonedDateTime = utcDateTime;
        this.inUTCMode = true;
        return this;
    }

    local = () => {
        const localTz = getLocalTimeZone();
        return gnoment(this).tz(localTz);
    };

    unix() {
        if (!this.isValid()) return NaN;
        const dUpdated = Math.floor(this.zonedDateTime.toDate().getTime() / 1000);
        return dUpdated;
    }

    valueOf() {
        if (!this.isValid()) return NaN;
        return gnoment(this.zonedDateTime).unix() * 1000;
    }

    utcOffset(offset, keepLocalTime = false) {
        // Warning only works with offset zero
        if (offset === 0) {
            if (keepLocalTime) {
                return gnoment(toZoned(toCalendarDateTime(this.zonedDateTime), 'UTC'));
            }
            return gnoment(this.zonedDateTime).utc();
        }
    }

    normalize(s) {
        let sUpdated = s;
        sUpdated = s.replace(/\+00:00\[UTC\]/g, 'Z');
        sUpdated = s.replace(/\[.*?\]/g, '');
        return sUpdated;
    }

    // Node specific
    [Symbol.for('nodejs.util.inspect.custom')]() {
        if (this.isValid()) return `Gnoment<${this.format()}>`;
        return 'Gnoment<Invalid date>';
    }

    toString() {
        if (this.isValid()) {
            return this.format(
                'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ',
                this.zonedDateTime.timeZone
            );
        }
        return 'Invalid date';
    }

    convertOffsetToHoursMinutes = (offset) => {
        const totalMinutes = Math.trunc(offset / 60000);
        const sign = totalMinutes >= 0 ? '+' : '-';
        const hours = Math.floor(Math.abs(totalMinutes) / 60);
        const minutes = Math.abs(totalMinutes) % 60;
        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(
            2,
            '0'
        )}`;
    };

    getOffset = () => {
        return this.convertOffsetToHoursMinutes(this.zonedDateTime.offset);
    };

    clone = () => {
        const cloned = new Gnoment(this.zonedDateTime);
        cloned._locale = this._locale;
        return cloned;
    };

    locale = (locale) => {
        if (locale === undefined) {
            return this._locale || defaultLocale;
        }
        this._locale = locale;
        return this;
    };

    isValid() {
        if (this.zonedDateTime) {
            return true;
        }
        return false;
    }

    //Date Comparisons
    isSame = (date2, unit) => {
        if (!this.isValid() || date2 === null) return false;

        // Handle unit comparison by starting at beginning of unit
        if (unit) {
            // Clone current date and start of unit
            const current = this.clone().startOf(unit);
            let dateToCompare;

            if (typeof date2 === 'string') {
                dateToCompare = gnoment(date2)
                    .tz(this.zonedDateTime?.timeZone)
                    .startOf(unit);
            } else {
                dateToCompare =
                    date2 === undefined
                        ? gnoment(undefined).startOf(unit)
                        : gnoment(date2).startOf(unit);
            }

            return !dateToCompare.zonedDateTime
                ? false
                : current.zonedDateTime.compare(dateToCompare.zonedDateTime) === 0;
        }

        if (typeof date2 === 'string') {
            return (
                this.zonedDateTime.compare(
                    gnoment(date2).tz(this.zonedDateTime?.timeZone).zonedDateTime
                ) === 0
            );
        }

        const dateToCompare =
            date2 === undefined ? gnoment(undefined).zonedDateTime : date2.zonedDateTime;

        return !dateToCompare ? false : this.zonedDateTime.compare(dateToCompare) === 0;
    };

    isSameOrBefore = (date2, unit) => {
        if (!this.isValid() || date2 === null) return false;

        // Handle unit comparison by starting at beginning of unit
        if (unit) {
            // Clone current date and start of unit
            const current = this.clone().startOf(unit);
            let dateToCompare;

            if (typeof date2 === 'string') {
                dateToCompare = gnoment(date2)
                    .tz(this.zonedDateTime?.timeZone)
                    .startOf(unit);
            } else {
                dateToCompare =
                    date2 === undefined
                        ? gnoment(undefined).startOf(unit)
                        : gnoment(date2).startOf(unit);
            }

            return !dateToCompare.zonedDateTime
                ? false
                : current.zonedDateTime.compare(dateToCompare.zonedDateTime) <= 0;
        }

        if (typeof date2 === 'string') {
            return (
                this.zonedDateTime.compare(
                    gnoment(date2).tz(this.zonedDateTime?.timeZone).zonedDateTime
                ) <= 0
            );
        }

        const dateToCompare =
            date2 === undefined ? gnoment(undefined).zonedDateTime : date2.zonedDateTime;

        return !dateToCompare ? false : this.zonedDateTime.compare(dateToCompare) <= 0;
    };

    isBefore = (date2, unit) => {
        if (!this.isValid() || date2 === null) return false;

        // Handle unit comparison by starting at beginning of unit
        if (unit) {
            // Clone current date and start of unit
            const current = this.clone().startOf(unit);
            let dateToCompare;

            if (typeof date2 === 'string') {
                dateToCompare = gnoment(date2)
                    .tz(this.zonedDateTime?.timeZone)
                    .startOf(unit);
            } else {
                dateToCompare =
                    date2 === undefined
                        ? gnoment(undefined).startOf(unit)
                        : gnoment(date2).startOf(unit);
            }

            return !dateToCompare.zonedDateTime
                ? false
                : current.zonedDateTime.compare(dateToCompare.zonedDateTime) < 0;
        }

        if (typeof date2 === 'string') {
            return (
                this.zonedDateTime.compare(
                    gnoment(date2).tz(this.zonedDateTime?.timeZone).zonedDateTime
                ) < 0
            );
        }

        const dateToCompare =
            date2 === undefined ? gnoment(undefined).zonedDateTime : date2.zonedDateTime;

        return !dateToCompare ? false : this.zonedDateTime.compare(dateToCompare) < 0;
    };

    isSameOrAfter = (date2, unit) => {
        if (!this.isValid() || date2 === null) return false;

        // Handle unit comparison by starting at beginning of unit
        if (unit) {
            // Clone current date and start of unit
            const current = this.clone().startOf(unit);
            let dateToCompare;

            if (typeof date2 === 'string') {
                dateToCompare = gnoment(date2)
                    .tz(this.zonedDateTime?.timeZone)
                    .startOf(unit);
            } else {
                dateToCompare =
                    date2 === undefined
                        ? gnoment(undefined).startOf(unit)
                        : gnoment(date2).startOf(unit);
            }

            return !dateToCompare.zonedDateTime
                ? false
                : current.zonedDateTime.compare(dateToCompare.zonedDateTime) >= 0;
        }

        if (typeof date2 === 'string') {
            return (
                this.zonedDateTime.compare(
                    gnoment(date2).tz(this.zonedDateTime?.timeZone).zonedDateTime
                ) >= 0
            );
        }

        const dateToCompare =
            date2 === undefined ? gnoment(undefined).zonedDateTime : date2.zonedDateTime;

        return !dateToCompare ? false : this.zonedDateTime.compare(dateToCompare) >= 0;
    };

    isAfter = (date2, unit) => {
        if (!this.isValid() || date2 === null) return false;

        // Handle unit comparison by starting at beginning of unit
        if (unit) {
            // Clone current date and start of unit
            const current = this.clone().startOf(unit);
            let dateToCompare;

            if (typeof date2 === 'string') {
                dateToCompare = gnoment(date2)
                    .tz(this.zonedDateTime?.timeZone)
                    .startOf(unit);
            } else {
                dateToCompare =
                    date2 === undefined
                        ? gnoment(undefined).startOf(unit)
                        : gnoment(date2).startOf(unit);
            }

            return !dateToCompare.zonedDateTime
                ? false
                : current.zonedDateTime.compare(dateToCompare.zonedDateTime) > 0;
        }

        if (typeof date2 === 'string') {
            return (
                this.zonedDateTime.compare(
                    gnoment(date2).tz(this.zonedDateTime?.timeZone).zonedDateTime
                ) > 0
            );
        }

        const dateToCompare =
            date2 === undefined ? gnoment(undefined).zonedDateTime : date2.zonedDateTime;

        return !dateToCompare ? false : this.zonedDateTime.compare(dateToCompare) > 0;
    };

    isBetween = (start, end, unit, inclusivity = '()') => {
        if (!this.isValid()) return false;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let currentUpdated = this;
        const timezone = this.zonedDateTime?.timeZone || defaultTimeZone;
        let startUpdated = gnoment(start).tz(timezone);
        let endUpdated = gnoment(end).tz(timezone);
        if (unit) {
            currentUpdated = this.startOf(unit);
            startUpdated = startUpdated.startOf(unit);
            endUpdated = endUpdated.startOf(unit);
        }
        const afterStart =
            inclusivity[0] === '['
                ? currentUpdated.isSameOrAfter(startUpdated)
                : currentUpdated.isAfter(startUpdated);
        const beforeEnd =
            inclusivity[1] === ']'
                ? currentUpdated.isSameOrBefore(endUpdated)
                : currentUpdated.isBefore(endUpdated);
        return afterStart && beforeEnd;
    };

    //Date Calcs
    diff = (date2, unit = 'milliseconds') => {
        if (!this.isValid()) return NaN;
        let res;
        if (unit === 'years') {
            res = differenceInYears(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'months') {
            res = differenceInMonths(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'weeks') {
            res = differenceInWeeks(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'days') {
            res = differenceInDays(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'hours') {
            res = differenceInHours(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'minutes') {
            res = differenceInMinutes(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'seconds') {
            res = differenceInSeconds(this.zonedDateTime.toDate(), new Date(date2));
        } else if (unit === 'milliseconds') {
            res = differenceInMilliseconds(this.zonedDateTime.toDate(), new Date(date2));
        }

        if (isNil(res)) {
            res = 0;
        }
        return res;
    };

    // moment units =>zonedDatetime units
    unitLookupTable = {
        month: 'months',
        day: 'days',
        d: 'days',
        year: 'years',
        y: 'years',
        week: 'weeks',
        hour: 'hours',
        minute: 'minutes',
        second: 'seconds',
    };

    unitLookup = (momentUnit) => {
        return momentUnit in this.unitLookupTable
            ? this.unitLookupTable[momentUnit]
            : momentUnit;
    };

    set = (...params) => {
        // Handle object param
        if (params.length === 1 && typeof params[0] === 'object' && params[0] !== null) {
            const updates = {};
            Object.entries(params[0]).forEach(([key, value]) => {
                const unitKey = key === 'date' ? 'day' : key;
                if (unitKey === 'month' || unitKey === 'months') {
                    updates[unitKey] = value + 1;
                } else {
                    updates[unitKey] = value;
                }
            });
            this.zonedDateTime = this.zonedDateTime.set(updates);
            return this;
        }

        // Handle individual unit/value pairs
        const [unit, value] = params;
        const unitUpdated = unit === 'date' ? 'day' : unit;
        if (unitUpdated && value !== undefined) {
            if (unitUpdated === 'month' || unitUpdated === 'months') {
                this.zonedDateTime = this.zonedDateTime.set({
                    [unitUpdated]: value + 1,
                });
            } else {
                this.zonedDateTime = this.zonedDateTime.set({ [unitUpdated]: value });
            }
        }

        return this;
    };

    day = (value) => {
        const isUTC = this.zonedDateTime.timeZone === 'UTC';
        const currentDate = this.zonedDateTime.toDate();
        //Note this only works with UTC or the local TZ due to what's available
        const currentDay = isUTC ? currentDate.getUTCDay() : currentDate.getDay();

        if (value === undefined) {
            return currentDay;
        }
        const diff = value - currentDay;

        return this.add(diff, 'day');
    };

    year = (y) => {
        if (y) {
            this.zonedDateTime = this.zonedDateTime.set({ year: y });
        }

        return Number(this.format('YYYY'));
    };

    month = (m) => {
        if (m) {
            this.zonedDateTime = this.zonedDateTime.set({
                month: m + 1,
            });
        }
        //Moment month is 0 indexed so we're following this behavior in gnoment
        const monthNum = Number(this.format('MM'));
        return monthNum - 1;
    };

    //Returns the day of the month, ranging from 1 to 31, representing the specific date within the month.
    date = (d) => {
        if (d) {
            this.zonedDateTime = this.zonedDateTime.set({ day: d });
        }

        return Number(this.format('DD'));
    };

    hour = (h) => {
        if (h !== undefined) {
            this.zonedDateTime = this.zonedDateTime.set({ hour: h });
            return this;
        }

        return Number(this.format('HH'));
    };

    minute = (m) => {
        if (m !== undefined) {
            this.zonedDateTime = this.zonedDateTime.set({ minute: m });
            return this;
        }

        return Number(this.format('mm'));
    };

    second = (s) => {
        if (s !== undefined) {
            this.zonedDateTime = this.zonedDateTime.set({ second: s });
            return this;
        }

        return Number(this.format('ss'));
    };

    //Warning just handling scenario without param due to usage in codebase
    isoWeekday = (value) => {
        if (!this.isValid()) return new Gnoment(null);

        const calDate = toCalendarDate(this.zonedDateTime);

        const jsDate = new Date(calDate.year, calDate.month - 1, calDate.day);

        const currentISO = jsDate.getDay() === 0 ? 7 : jsDate.getDay();

        //Returns the current ISO weekday (Monday = 1, Sunday = 7)
        if (value === undefined) {
            return currentISO;
        }
    };

    //Warning just handling scenario without param due to usage in codebase
    weekday = (value) => {
        if (!this.isValid()) return new Gnoment(null);

        const isUTC = this.zonedDateTime.timeZone === 'UTC';
        const jsDate = this.zonedDateTime.toDate();
        if (value === undefined) {
            //Note this only works with UTC or the local TZ due to what's available
            return isUTC ? jsDate.getUTCDay() : jsDate.getDay();
        }
    };

    add = (duration, unit) => {
        if (!this.isValid()) return new Gnoment(null);
        const unitTemp = this.unitLookup(unit);
        this.zonedDateTime = this.zonedDateTime.add({ [unitTemp]: duration });
        return this;
    };

    subtract = (duration, unit) => {
        if (!this.isValid()) return new Gnoment(null);
        const unitTemp = this.unitLookup(unit);
        this.zonedDateTime = this.zonedDateTime.subtract({ [unitTemp]: duration });
        return this;
    };

    startOf = (unit) => {
        if (!this.isValid()) return new Gnoment(null);
        let res;
        const isUTC = this.zonedDateTime.timeZone === 'UTC' || this.inUTCMode;
        if (unit === 'year' || unit === 'years') {
            let temp = startOfYear(this.zonedDateTime);
            temp = temp.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = gnoment(temp);
        } else if (unit === 'month' || unit === 'months') {
            let temp = startOfMonth(this.zonedDateTime);
            temp = temp.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = gnoment(temp);
        } else if (unit === 'isoWeek') {
            let temp = this.zonedDateTime;
            //Note this only works with UTC or the local TZ due to what's available
            const currentDay = isUTC ? temp.toDate().getUTCDay() : temp.toDate().getDay();
            // Calculate days to subtract to get to Monday (ISO week starts on Monday)
            const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1;
            temp = temp.subtract({ days: daysToSubtract });
            temp = temp.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = gnoment(temp);
        } else if (unit === 'week') {
            let temp = this.zonedDateTime;
            const currentDay = isUTC ? temp.toDate().getUTCDay() : temp.toDate().getDay();
            // Subtract days to get to start of week (Sunday)
            temp = temp.subtract({ days: currentDay });
            temp = temp.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = gnoment(temp);
        } else if (unit === 'day' || unit === 'days') {
            let temp = this.zonedDateTime;
            temp = temp.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = gnoment(temp);

            if (this.inUTCMode) {
                res.inUTCMode = true;
            }
        } else if (unit === 'hour' || unit === 'hours') {
            let temp = this.zonedDateTime;
            temp = temp.set({ minute: 0, second: 0, millisecond: 0 });
            res = gnoment(temp);
        } else if (unit === 'minute' || unit === 'minutes') {
            let temp = this.zonedDateTime;
            temp = temp.set({ second: 0, millisecond: 0 });
            res = gnoment(temp);
        } else if (unit === 'seconds' || unit === 'second') {
            let temp = this.zonedDateTime;
            temp = temp.set({ millisecond: 0 });
            res = gnoment(temp);
        }

        return res;
    };

    endOf = (unit) => {
        if (!this.isValid()) return new Gnoment(null);
        let res;
        if (unit === 'year') {
            let temp = endOfYear(this.zonedDateTime);
            temp = temp.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
            res = gnoment(temp);
        } else if (unit === 'month') {
            let temp = endOfMonth(this.zonedDateTime);
            temp = temp.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
            res = gnoment(temp);
        } else if (unit === 'day') {
            let temp = this.zonedDateTime;
            temp = temp.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
            res = gnoment(temp);
        }
        return res;
    };

    tz = (timezone, keepLocalTime = false) => {
        if (!this.isValid()) return this;
        if (!timezone?.trim()) return undefined;
        if (this.inUTCMode && timezone !== 'UTC') {
            this.inUTCMode = false;
        }
        if (keepLocalTime) {
            // Keep local time by getting current components and creating new date in target timezone
            const current = this.zonedDateTime;
            const newDate = new CalendarDateTime(
                current.year,
                current.month,
                current.day,
                current.hour,
                current.minute,
                current.second,
                current.millisecond
            );
            // Changes timezone keeping original time
            this.zonedDateTime = toZoned(newDate, timezone);
        } else {
            this.zonedDateTime = toZoned(this.zonedDateTime, timezone);
        }

        return this;
    };

    toISOString() {
        return gnoment(this.zonedDateTime).utc().format('YYYY-MM-DDTHH:mm:ss.sss[Z]');
    }

    format = (format, timezone) => {
        if (!this.isValid()) return 'Invalid date';

        // Create date object and handle timezone if provided
        let tzUpdated = timezone;
        let formatUpdated = format;

        if (!formatUpdated) {
            formatUpdated = this.inUTCMode
                ? 'YYYY-MM-DDTHH:mm:ss[Z]'
                : 'YYYY-MM-DDTHH:mm:ssZ';
        }

        if (!tzUpdated) tzUpdated = this.zonedDateTime.timeZone;

        const date = this.zonedDateTime.toDate(tzUpdated);
        const d = new Date(date);
        let timezoneShort = '';

        const getDateEnding = (day) => {
            if (day % 100 >= 11 && day % 100 <= 13) {
                return 'th';
            }
            switch (day % 10) {
                case 1:
                    return 'st';
                case 2:
                    return 'nd';
                case 3:
                    return 'rd';
                default:
                    return 'th';
            }
        };

        // Helper function to get timezone offset in format ±HH:mm
        const getTimezoneOffset = () => {
            return this.convertOffsetToHoursMinutes(this.zonedDateTime.offset);
        };

        const resolvedLocale = this._locale || defaultLocale;

        const getCachedFormatter = (options) => {
            const key = JSON.stringify({
                locale: resolvedLocale,
                timeZone: tzUpdated,
                ...options,
            });

            if (!Gnoment.formatterCache[key]) {
                Gnoment.formatterCache[key] = new Intl.DateTimeFormat(resolvedLocale, {
                    ...options,
                    timeZone: tzUpdated,
                });
            }
            return Gnoment.formatterCache[key];
        };

        const formatDate = (options) => getCachedFormatter(options).format(d);
        // Extract the part corresponding to the timezone name

        const getShortTZ = () => {
            const parts = new Intl.DateTimeFormat(resolvedLocale, {
                timeZone: tzUpdated,
                timeZoneName: 'short',
            }).formatToParts(date);
            const tzNamePart = parts.find((part) => part.type === 'timeZoneName');
            timezoneShort = tzNamePart ? tzNamePart.value : '';
            return timezoneShort;
        };

        const YYYYFormat = formatDate({
            year: 'numeric',
        });

        const formatTokens = {
            // Year
            YYYY: () => YYYYFormat.padStart(4, '0'),
            YY: () => formatDate({ year: '2-digit' }),

            // Month
            MM: () => formatDate({ month: '2-digit' }),
            M: () => formatDate({ month: 'numeric' }),
            MMMM: () => formatDate({ month: 'long' }),
            MMM: () => formatDate({ month: 'short' }),

            // Day
            DD: () => formatDate({ day: '2-digit' }),
            Do: () => {
                const formattedDay = formatDate({ day: 'numeric' });
                return formattedDay.concat(getDateEnding(Number(formattedDay)));
            },
            D: () => formatDate({ day: 'numeric' }),

            dddd: () => formatDate({ weekday: 'long' }),
            ddd: () => formatDate({ weekday: 'short' }),

            // Hour
            HH: () =>
                formatDate({
                    hour: '2-digit',
                    hour12: false,
                }).replace(/24/g, '00'),
            H: () =>
                formatDate({
                    hour: 'numeric',
                    hour12: false,
                }).replace(/24/g, '0'),
            hh: () =>
                formatDate({
                    hour: '2-digit',
                    hour12: true,
                }).replace(/ AM| PM/g, ''),
            h: () =>
                formatDate({
                    hour: 'numeric',
                    hour12: true,
                }).replace(/ AM| PM/g, ''),

            // Minute
            mm: () => formatDate({ minute: '2-digit' }).padStart(2, '0'),
            m: () => formatDate({ minute: 'numeric' }),

            ss: () => formatDate({ second: '2-digit' }).padStart(2, '0'),
            s: () => formatDate({ second: 'numeric' }),

            // Millisecond
            sss: () =>
                formatDate({
                    millisecond: 'short',
                }),
            SSS: () =>
                formatDate({
                    fractionalSecondDigits: 3,
                }).split('.')[1] || '000',
            // AM/PM
            A: () =>
                formatDate({
                    hour12: true,
                    hour: 'numeric',
                }).split(' ')[1],
            a: () =>
                formatDate({
                    hour12: true,
                    hour: 'numeric',
                })
                    .split(' ')[1]
                    .toLowerCase(),

            // Timezone
            ZZ: () => getTimezoneOffset(d, tzUpdated).replace(':', ''),
            Z: () => getTimezoneOffset(d, tzUpdated),
            z: () => getShortTZ(d, tzUpdated) || defaultTimeZone,
        };

        // Replace locale-aware L-format tokens
        // en-US uses hardcoded expansions to match moment (Intl adds "at" between date/time)
        const enUSFormats = {
            LTS: 'h:mm:ss A',
            LT: 'h:mm A',
            LLLL: 'dddd, MMMM D, YYYY h:mm A',
            LLL: 'MMMM D, YYYY h:mm A',
            LL: 'MMMM D, YYYY',
            L: 'MM/DD/YYYY',
            llll: 'ddd, MMM D, YYYY h:mm A',
            lll: 'MMM D, YYYY h:mm A',
            ll: 'MMM D, YYYY',
            l: 'M/D/YYYY',
        };
        const intlFormats = {
            LTS: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
            LT: { hour: 'numeric', minute: '2-digit' },
            LLLL: {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            },
            LLL: {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            },
            LL: { year: 'numeric', month: 'long', day: 'numeric' },
            L: { year: 'numeric', month: '2-digit', day: '2-digit' },
            llll: {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            },
            lll: {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            },
            ll: { year: 'numeric', month: 'short', day: 'numeric' },
            l: { year: 'numeric', month: 'numeric', day: 'numeric' },
        };
        const localeFormatOrder = [
            'LTS',
            'LT',
            'LLLL',
            'LLL',
            'LL',
            'L',
            'llll',
            'lll',
            'll',
            'l',
        ];
        for (const token of localeFormatOrder) {
            if (formatUpdated.includes(token)) {
                if (resolvedLocale === 'en-US') {
                    formatUpdated = formatUpdated.replace(token, enUSFormats[token]);
                } else {
                    const formatted = new Intl.DateTimeFormat(resolvedLocale, {
                        ...intlFormats[token],
                        timeZone: tzUpdated,
                    }).format(d);
                    formatUpdated = formatUpdated.replace(token, `[${formatted}]`);
                }
            }
        }

        if (formatUpdated.includes('x')) {
            formatUpdated = formatUpdated.replace('x', String(gnoment(d).unix() * 1000));
        }

        return formatUpdated.replace(
            formatRegex,
            (match, contents) => contents || formatTokens[match]()
        );
    };

    toJSON() {
        if (!this.isValid()) return null;
        return (this.inUTCMode ? this : this.utc()).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }

    toDate() {
        if (!this.isValid()) return null;
        return new Date(this);
    }
}

export function gnoment(d) {
    return new Gnoment(d);
}

gnoment.unix = (d) => {
    let dUpdated = d;
    if (dUpdated === undefined || (typeof dUpdated !== 'number' && dUpdated !== null)) {
        return new Gnoment(null);
    }
    if (dUpdated === null) dUpdated = 0;
    const dateTime = fromAbsolute(dUpdated * 1000, defaultTimeZone);
    return gnoment(dateTime);
};

gnoment.isGnoment = (date) => {
    return date instanceof Gnoment;
};

gnoment.parseZone = (input) => {
    let zonedDateTime;
    const found = input.match(zoneOffset);
    if (input.includes('Z')) {
        zonedDateTime = parseAbsolute(input, 'UTC');
    } else if (found) {
        zonedDateTime = parseAbsolute(input, found[1]);
    } else {
        //Todo: add test case if you run into this
        return gnoment(input);
    }

    return gnoment(zonedDateTime);
};

gnoment.min = (...params) => {
    if (params.length === 1) {
        params = params[0];
    }
    if (params.length === 0) {
        return gnoment();
    }
    const gnoments = params.map((d) => gnoment(d));
    let minDate = gnoments[0];
    for (let i = 1; i < gnoments.length; i += 1) {
        if (gnoments[i].isBefore(minDate)) {
            minDate = gnoments[i];
        }
    }
    return minDate;
};

gnoment.max = (...params) => {
    if (params.length === 1) {
        params = params[0];
    }
    if (params.length === 0) {
        return gnoment();
    }
    const gnoments = params.map((d) => gnoment(d));
    let maxDate = gnoments[0];
    for (let i = 1; i < gnoments.length; i += 1) {
        if (gnoments[i].isAfter(maxDate)) {
            maxDate = gnoments[i];
        }
    }
    return maxDate;
};

gnoment.tz.guess = () => {
    return getLocalTimeZone();
};
